import * as React from 'react'
import { useCallback, useMemo, useImperativeHandle, forwardRef } from 'react'
import { Pressable, StyleProp, View, ViewStyle } from 'react-native'
import { Text } from 'react-native-paper'
import { TutorialWrapper as TutorialComponent } from '../../../../../@ui/containers/others/tutorial/tutorial.wrapper'
import { JustificationModal } from './vote-justification.modal'
import { VoteDetailsModal } from './vote.provider'
import { ArticleVote } from './types'
import { useNavigation } from '@react-navigation/native'
import { useTheme } from '../../../../../hooks/theme.hook'
import { IconVote } from '../../../../../utils/meldd-icons'
import { useAuthContext } from '../../../../../contexts/auth-context'
import { AppNavigation } from '../../../../../navigation/types'
import { createStyle } from '../../../../../contexts/global-styles.provider'
import { useVoteActions, type VoteChangePayload } from './use-vote-actions.hook'
import {
  RipplePopIconHost,
  ScaleGlowWrap,
  useRipplePopPulse,
} from '../../../../../@ui'

export type { VoteChangePayload }

export type VoteButtonCluster = { id: string; articleVotes: ArticleVote[] }
export type VoteButtonPerspective = {
  id: string
  title: string
  voteCount: number
  hasUserVote: boolean
}

export type VoteButtonRef = {
  triggerVote: () => void
}

interface VoteButtonProps {
  perspective: VoteButtonPerspective
  mode?: 'horizontal' | 'vertical'
  style?: StyleProp<ViewStyle>
  readonly?: boolean
  onVoteChange?: (payload: VoteChangePayload) => void
}

export const VoteButton = forwardRef<VoteButtonRef, VoteButtonProps>(
  function VoteButton({ perspective, style, readonly, onVoteChange }, ref) {
    const theme = useTheme()
    const { userId, isAnonymous, openLoginModal } = useAuthContext()
    const [openJustificationModal, setOpenJustification] = React.useState(false)
    const [showVoteDetails, setShowVoteDetails] = React.useState(false)
    const navigator = useNavigation<AppNavigation>()
    const styles = useStyle()
    const { generation, trigger } = useRipplePopPulse()

    const handleVoteChange = useCallback(
      (payload: VoteChangePayload) => {
        if (payload.added) {
          trigger()
          setOpenJustification(false)
        }
        onVoteChange?.(payload)
      },
      [onVoteChange, trigger]
    )

    const hasMyVote = useMemo(
      () => perspective.hasUserVote,
      [perspective.hasUserVote]
    )

    const { placeVote, removeVote, loading } = useVoteActions({
      articleId: perspective.id,
      onVoteChange: handleVoteChange,
    })

    const onPressVote = () => {
      if (isAnonymous) {
        openLoginModal(navigator)
        return
      }
      if (hasMyVote) {
        removeVote()
        return
      }
      setOpenJustification(true)
    }

    useImperativeHandle(ref, () => ({
      triggerVote: () => {
        if (isAnonymous) {
          openLoginModal(navigator)
          return
        }
        if (hasMyVote) {
          removeVote()
          return
        }
        setOpenJustification(true)
      },
    }))

    return (
      <View style={[styles.horizontal, style]}>
        {!readonly ? (
          <Pressable
            style={({ pressed }) => [
              styles.iconButton,
              pressed && styles.pressed,
            ]}
            onPress={() => {
              if (!readonly) {
                onPressVote()
              } else {
                setShowVoteDetails(true)
              }
            }}
          >
            <TutorialComponent tutorialId="vote_perspective" offsetX={28}>
              <RipplePopIconHost
                generation={generation}
                accentColor={theme.colors.primary}
              >
                <View style={{ left: 2 }}>
                  <IconVote
                    mode="text"
                    size={20}
                    loading={loading}
                    onPress={() => onPressVote()}
                    selected={!hasMyVote}
                  />
                </View>
              </RipplePopIconHost>
            </TutorialComponent>
          </Pressable>
        ) : (
          <View>
            <IconVote
              mode="text"
              size={20}
              loading={loading}
              selected={hasMyVote}
            />
          </View>
        )}
        <Pressable
          style={[styles.horizontalText]}
          onPress={() => setShowVoteDetails(true)}
        >
          <ScaleGlowWrap
            generation={generation}
            accentColor={theme.colors.primary}
          >
            <Text
              style={{
                textAlign: 'center',
                color: theme.colors.primary,
                fontSize: 14,
                fontWeight: '500',
                textDecorationLine:
                  perspective.voteCount > 0 ? 'underline' : 'none',
              }}
            >
              {perspective.voteCount}
            </Text>
          </ScaleGlowWrap>
        </Pressable>

        <JustificationModal
          open={openJustificationModal}
          perspective={perspective}
          onDismiss={() => setOpenJustification(false)}
          vote={(justification) => {
            void placeVote(justification)
          }}
        />
        <VoteDetailsModal
          visible={showVoteDetails}
          article={perspective}
          onDismiss={() => setShowVoteDetails(false)}
          onVotePress={() => {
            setShowVoteDetails(false)
            onPressVote()
          }}
        />
      </View>
    )
  }
)

const useStyle = createStyle(({ theme }) => ({
  vertical: {
    position: 'relative',
    alignItems: 'center',
    color: theme.colors.primary,
    rowGap: theme.spacing(0),
  },
  horizontal: {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: theme.spacing(1),
    overflow: 'visible',
  },
  iconButton: {
    padding: 8,
    borderRadius: theme.spacing(1.5),
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    borderWidth: 1,
    borderColor: 'rgba(0, 0, 0, 0.1)',
  },
  horizontalText: {
    position: 'relative',
    minWidth: 20,
    paddingHorizontal: 4,
    paddingVertical: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pressed: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    transform: [{ scale: 0.98 }],
  },
  verticalText: {
    position: 'relative',
    borderColor: 'rgba(239, 239, 240, 1)',
    backgroundColor: 'rgba(239, 239, 240, 0.7)',
    height: 35,
    width: 35,
    borderRadius: 100,
    justifyContent: 'center',
  },
}))
